import * as types from "./actionTypes";
import * as queueApi from "../api/queueApi";
import * as socket from "../realtime/socketClient";
import { failureMessage, isForbidden } from "../api/client";
import { clearHostToken, setHostToken } from "../session/hostTokens";
import { setHostKey } from "../session/hostKey";
import { showNotice } from "./noticeActions";
import { loadQueueSongs, queueSongSung } from "./queueActions";
import { selectNextQueueSong, selectPreviousQueueSong, selectQueueSongs } from "../selectors";
import { loadLibrary } from "./libraryActions";
import { loadHostProfile } from "./hostActions";
import { loadRequests } from "./requestsActions";
import { sortedVerses, verseLabel } from "../lib/lyrics";

const REFRESH_THROTTLE_MS = 3000;

const isBrowserOnline = () => typeof navigator === "undefined" || navigator.onLine !== false;

// A request that never got an answer (no network, timeout), as opposed to a refusal.
const isOffline = (error) => !error?.response;

/**
 * Makes a session the current one. Re-selecting the code that is already current keeps the
 * loaded song/queue state (only errors are cleared); a different code resets it so the new
 * session's data can be loaded.
 */
export const setSessionCode = (code) => (dispatch, getState) => {
  if (getState().session.Code === code) {
    dispatch(sessionError(null));
    return;
  }
  dispatch({ type: types.SESSION_SET_CODE, code });
};

export const clearSession = () => ({ type: types.SESSION_CLEAR });

/** The session was ended by its host (pushed by the server, or done here by the host). */
export const sessionEnded = (code) => ({ type: types.SESSION_ENDED, code });

/** The section the host kept on screens longest, from this device's own clock. */
const heldLongest = (state) => {
  const times = state.session.sectionTimes || {};
  let best = null;
  for (const [key, seconds] of Object.entries(times)) {
    if (!best || seconds > best.seconds) best = { key, seconds };
  }
  if (!best) return null;
  const [songId, verseNum] = best.key.split(":").map(Number);
  const song = state.songs[songId];
  if (!song) return null;
  const verses = sortedVerses(song);
  const index = verses.findIndex((verse) => verse.Order === verseNum);
  return {
    SongTitle: song.Title,
    Section: index >= 0 ? verseLabel(verses[index], index) : `Section ${verseNum + 1}`,
    Seconds: Math.round(best.seconds),
  };
};

/**
 * Deletes the session for everyone (host only) and keeps the server's summary for the ended
 * page. What this device knows (the set, the songs, the section held longest) is put in
 * place first: the server's "session ended" push can arrive before its answer, and the app
 * must already know a summary is on its way when it leaves the session.
 */
export const endSession = (code) => async (dispatch, getState) => {
  const state = getState();
  const songs = selectQueueSongs(state);
  const local = {
    Code: code,
    SetId: state.session.SetId,
    SetName: state.session.SetName,
    Reactions: state.session.Reactions,
    SongsQueued: songs.length,
    SongIds: songs.map((song) => song.Id),
    SongTitles: songs.map((song) => song.Title),
    HeldLongest: heldLongest(state),
  };
  dispatch({ type: types.SUMMARY_SET, summary: { ...local, pending: true } });
  try {
    const summary = await queueApi.endSession(code);
    const fromServer = summary && typeof summary === "object" ? summary : {};
    dispatch({
      type: types.SUMMARY_SET,
      summary: {
        ...local,
        ...fromServer,
        Code: fromServer.Code || code,
        SongIds: fromServer.SongIds?.length ? fromServer.SongIds : local.SongIds,
        SetName: fromServer.SetName ?? local.SetName,
        pending: false,
      },
    });
    clearHostToken(code);
    dispatch(sessionEnded(code));
  } catch (error) {
    dispatch(clearSummary());
    dispatch(
      showNotice(
        isForbidden(error)
          ? "Only the host can end the session."
          : failureMessage(error, "Couldn't end the session. Check your connection and try again.")
      )
    );
  }
};

export const clearSummary = () => ({ type: types.SUMMARY_CLEAR });

/** Records a message for the home page, e.g. why a session could not be joined. */
export const sessionError = (message) => ({ type: types.SESSION_ERROR, message });

export const setFollowLyrics = (followState) => ({ type: types.SESSION_SET_FOLLOW, followState });

// pushed by the server (or applied optimistically before the server confirms)
export const sessionSongChanged = (SongId, VerseNum) => ({ type: types.SESSION_SET_SONG, SongId, VerseNum });
export const sessionVerseChanged = (VerseNum) => ({ type: types.SESSION_SET_VERSE, VerseNum });
export const sessionReactions = (reactions) => ({ type: types.SESSION_REACTIONS, reactions });
export const sessionListeners = (count) => ({ type: types.SESSION_LISTENERS, count });

/** A guest looks at another section than the live one; null goes back to live. */
export const peekVerse = (verseNum) => ({ type: types.SESSION_PEEK, verseNum });

/** A heart from this device: counted at once, confirmed by the server's count. */
export const sendReaction = (code) => async (dispatch, getState) => {
  const { session } = getState();
  if (session.Code !== code || session.ended) return;
  dispatch(sessionReactions(session.Reactions + 1));
  try {
    const result = await queueApi.react(code);
    if (getState().session.Code === code && result?.Reactions != null) dispatch(sessionReactions(result.Reactions));
  } catch {
    // hearts are best effort; the next session load carries the real count
  }
};

// --- loading -----------------------------------------------------------------------------

let loadSessionSequence = 0;

/** Loads the session's current song/verse. Resolves with the session, or null when it no longer exists. */
export const loadSession = (code) => async (dispatch) => {
  const sequence = ++loadSessionSequence;
  try {
    const session = await queueApi.getQueue(code);
    // a slower, older request must not overwrite a newer answer
    if (sequence === loadSessionSequence) dispatch({ type: types.SESSION_LOAD_SUCCESS, session });
    return session;
  } catch (error) {
    const notFound = error.response?.status === 404;
    dispatch({ type: types.SESSION_LOAD_FAIL, code, notFound });
    if (!notFound) console.error("Could not load session", error);
    return null;
  }
};

let lastRefreshAt = 0;

/**
 * Re-fetches everything the app shows for the current session. Used to catch up after a
 * reconnect or after the page was in the background; throttled unless forced.
 */
export const refreshSessionData = ({ force = false } = {}) => async (dispatch, getState) => {
  const code = getState().session.Code;
  if (!code) return;
  const now = Date.now();
  if (!force && now - lastRefreshAt < REFRESH_THROTTLE_MS) return;
  lastRefreshAt = now;
  // changes the host made offline go out first, so the reload below does not undo them
  if (getState().session.pendingSelection) await dispatch(flushPendingSelection(code));
  const active = getState().session;
  if (active.Code !== code || active.pendingSelection || active.ended) return;
  dispatch(loadSession(code));
  dispatch(loadQueueSongs(code));
  dispatch(loadLibrary(code));
  dispatch(loadRequests(code));
};

/** Makes a session this device just created (or was handed) the current one, as its host. */
export const adoptCreatedSession = (session) => (dispatch) => {
  setHostToken(session.Code, session.HostToken);
  dispatch(setSessionCode(session.Code));
  dispatch({ type: types.SESSION_LOAD_SUCCESS, session });
  // creating a session registers this device as a host when it had no key yet
  if (session.HostKey) {
    setHostKey(session.HostKey);
    dispatch(loadHostProfile());
  }
  return session;
};

/** Creates a new session on the server and makes it the current one. */
export const createSession = () => async (dispatch) => {
  const session = await queueApi.createQueue();
  return dispatch(adoptCreatedSession(session));
};

// --- real-time connection ------------------------------------------------------------------

export const connectionStateChanged = (state) => ({ type: types.SOCKET_STATE_CHANGED, state });

export const onlineChanged = (isOnline) => ({ type: types.ONLINE_CHANGED, isOnline });

/** Opens the real-time connection; the client keeps retrying by itself until it succeeds. */
export const connect = () => () => socket.start().catch(() => {});

/**
 * The page is back in the foreground or the network returned: make sure the connection is
 * alive (reconnecting at once if not) and catch up on anything that happened meanwhile.
 */
export const resume = ({ hiddenMs = 0 } = {}) => async (dispatch) => {
  const online = isBrowserOnline();
  dispatch(onlineChanged(online));
  if (!online) return;

  const wasConnected = socket.isConnected();
  const connected = await socket.resume({ hiddenMs });
  // A reconnect triggers a full refresh through the state change (see AppInit); after a
  // successful ping on an existing connection just catch up quietly.
  if (connected && wasConnected) dispatch(refreshSessionData());
};

/** Joins the session's real-time group. The server answers false for unknown codes. */
export const joinSession = (code) => async (dispatch) => {
  dispatch({ type: types.SESSION_JOINING, code });
  try {
    const joined = await socket.invoke("JoinSession", code);
    dispatch({ type: types.SESSION_JOINED, code, result: joined });
  } catch (error) {
    dispatch({ type: types.SESSION_JOIN_FAILED, code });
  }
};

// --- changing the session ------------------------------------------------------------------
// Applied optimistically so the host sees the change at once even on a slow link; the server
// pushes the same change to everyone (including us) and the UI is reloaded if it failed.

export const selectSong = (code, songId, verseNum = -1) => async (dispatch, getState) => {
  if (getState().session.Code !== code) return;
  const replaying = Boolean(getState().session.pendingSelection);
  dispatch({ ...sessionSongChanged(songId, verseNum), local: true });
  if (replaying) {
    dispatch(rememberPendingSelection());
    return dispatch(flushPendingSelection(code));
  }
  try {
    await queueApi.setSong(code, songId);
    if (verseNum >= 0 && getState().session.Code === code) await queueApi.setVerse(code, verseNum);
  } catch (error) {
    if (getState().session.Code !== code) return;
    if (isOffline(error)) {
      dispatch(rememberPendingSelection());
      return;
    }
    dispatch(
      showNotice(
        isForbidden(error)
          ? "Only the host can change the song."
          : failureMessage(error, "Couldn't change the song. Check your connection and try again.")
      )
    );
    dispatch(loadSession(code));
  }
};

/** Moves the session on to the next unsung song in the queue; the one being left is marked sung. */
export const selectNextSong = (code) => (dispatch, getState) => {
  const state = getState();
  const next = selectNextQueueSong(state);
  if (!next) return;
  const current = state.session.SongId;
  if (current && state.songs[current]) dispatch(queueSongSung(current, true));
  dispatch(selectSong(code, next.Id, 0));
};

/** Goes back to the previous song in the queue. */
export const selectPreviousSong = (code) => (dispatch, getState) => {
  const previous = selectPreviousQueueSong(getState());
  if (previous) dispatch(selectSong(code, previous.Id, 0));
};

export const selectVerse = (code, verseNum) => async (dispatch, getState) => {
  if (getState().session.Code !== code) return;
  const replaying = Boolean(getState().session.pendingSelection);
  dispatch({ ...sessionVerseChanged(verseNum), local: true });
  if (replaying) {
    dispatch(rememberPendingSelection());
    return dispatch(flushPendingSelection(code));
  }
  try {
    await queueApi.setVerse(code, verseNum);
  } catch (error) {
    if (getState().session.Code !== code) return;
    if (isOffline(error)) {
      dispatch(rememberPendingSelection());
      return;
    }
    dispatch(
      showNotice(
        isForbidden(error)
          ? "Only the host can change the verse."
          : failureMessage(error, "Couldn't change the verse. Check your connection and try again.")
      )
    );
    dispatch(loadSession(code));
  }
};

// --- offline host changes -------------------------------------------------------------------

/**
 * The host changed the song or verse but the server could not be reached: keep the change
 * (it is already applied locally), remember it for when the connection is back, and say so
 * once.
 */
export const rememberPendingSelection = () => (dispatch, getState) => {
  const { SongId, VerseNum, pendingSelection, isHost, Code, ended } = getState().session;
  if (!Code || !isHost || ended) return;
  if (!pendingSelection) {
    dispatch(showNotice("You're offline. Your changes will be sent when the connection is back.", "info"));
  }
  dispatch({ type: types.SESSION_PENDING_SELECTION, SongId, VerseNum });
};

const pendingFlushes = new WeakMap(); // one serialized replay per store/session

/** Drains the latest host intent in order. Concurrent callers join the same replay. */
export const flushPendingSelection = (code) => (dispatch, getState) => {
  const existing = pendingFlushes.get(getState);
  if (existing?.code === code) return existing.promise;
  const eligible = () => {
    const session = getState().session;
    return session.Code === code && session.isHost && !session.ended && !session.notFound;
  };
  const flush = async () => {
    while (eligible()) {
      const pending = getState().session.pendingSelection;
      if (!pending) return true;
      const current = () => eligible() && getState().session.pendingSelection === pending;
      try {
        await queueApi.setSong(code, pending.SongId ?? 0);
        if (!eligible()) return false;
        if (!current()) continue; // do not apply an old verse to a newer song
        if (pending.SongId) await queueApi.setVerse(code, pending.VerseNum);
        if (current()) dispatch({ type: types.SESSION_PENDING_SENT });
      } catch (error) {
        if (!isOffline(error) && current()) {
          dispatch({ type: types.SESSION_PENDING_SENT });
          dispatch(showNotice(failureMessage(error, "The pending selection could not be sent.")));
        }
        return false;
      }
    }
    return false;
  };
  const promise = flush();
  const entry = { code, promise };
  pendingFlushes.set(getState, entry);
  void promise.finally(() => { if (pendingFlushes.get(getState) === entry) pendingFlushes.delete(getState); });
  return promise;
};
